import { z } from 'zod';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { TransportOffering } from '@/features/dashboard/tourTransport/types/TransportOffering.ts';
import { TransportSelectionMode } from '@/features/dashboard/tourTransport/types/TransportSelectionMode.ts';
import { ParticipantLimitMode } from '@/features/dashboard/tourTransport/types/ParticipantLimitMode.ts';
import { hasAgeRangeOverlap } from '@/schemas/ageRangeOverlapValidation.ts';
import { Territory } from '@/features/dashboard/tfg/types/Territory.ts';
import { UfgTransportType } from '@/features/dashboard/tfg/types/UfgTransportType.ts';
import { isFlightType } from '@/features/dashboard/tfg/types/UfgTransportType.utils.ts';

const transportAgePriceSchema = z
  .object({
    minAge: z.coerce
      .number({ error: 'Wiek musi być liczbą' })
      .int('Wiek nie może być ułamkiem')
      .nonnegative('Wiek minimalny musi być większy lub równy 0')
      .optional(),
    maxAge: z.coerce
      .number({ error: 'Wiek musi być liczbą' })
      .int('Wiek nie może być ułamkiem')
      .positive('Wiek maksymalny musi być większy od 0')
      .optional(),
    amount: z.coerce
      .number({ error: 'Cena musi być liczbą' })
      .nonnegative('Cena musi być większa lub równa 0'),
  })
  .refine(
    (data) =>
      data.minAge == null || data.maxAge == null || data.minAge <= data.maxAge,
    {
      message: 'Wiek minimalny nie może być większy od maksymalnego',
      path: ['maxAge'],
    },
  );

const transportVariantSchema = z
  .object({
    id: z.string().optional(),
    name: z
      .string({ error: 'Nazwa wariantu jest wymagana' })
      .min(1, 'Nazwa wariantu jest wymagana')
      .max(255, 'Nazwa wariantu nie może przekraczać 255 znaków'),
    description: z.string().optional(),
    participantLimitMode: z
      .enum(ParticipantLimitMode)
      .default(ParticipantLimitMode.UNLIMITED),
    participantLimit: z.coerce
      .number({ error: 'Liczba miejsc musi być liczbą' })
      .int('Liczba miejsc nie może być ułamkiem')
      .positive('Liczba miejsc musi być większa od 0')
      .optional(),
    currency: z
      .enum(Currency, { error: 'Wybór waluty jest wymagany' })
      .default(Currency.PLN),
    isPriceByAge: z.boolean().default(false),
    flatPrice: z.coerce
      .number({ error: 'Cena musi być liczbą' })
      .nonnegative('Cena musi być większa lub równa 0')
      .optional(),
    ageRangePrices: z.array(transportAgePriceSchema),
    // TFG regulatory typing (see features/dashboard/tfg)
    ufgTransportType: z.enum(UfgTransportType).optional(),
    airportCode: z.string().optional(),
  })
  .refine(
    (data) =>
      data.participantLimitMode !== ParticipantLimitMode.LIMITED ||
      (data.participantLimit ?? 0) >= 1,
    {
      message: 'Liczba miejsc musi być większa od 0',
      path: ['participantLimit'],
    },
  )
  .refine(
    (data) =>
      data.isPriceByAge ||
      (data.flatPrice !== undefined && data.flatPrice >= 0),
    {
      message: 'Cena jest wymagana',
      path: ['flatPrice'],
    },
  )
  .refine((data) => !data.isPriceByAge || data.ageRangePrices.length >= 1, {
    message: 'Dodaj co najmniej jedną grupę wiekową',
    path: ['ageRangePrices'],
  })
  .refine(
    (data) =>
      !data.isPriceByAge ||
      data.ageRangePrices.length < 2 ||
      !hasAgeRangeOverlap(data.ageRangePrices),
    {
      message: 'Zakresy wiekowe nie mogą się nakładać',
      path: ['ageRangePrices'],
    },
  )
  .refine((data) => data.ufgTransportType != null, {
    message: 'Wybierz typ transportu',
    path: ['ufgTransportType'],
  })
  .refine(
    (data) =>
      !isFlightType(data.ufgTransportType) ||
      (data.airportCode != null && data.airportCode.length > 0),
    {
      message: 'Wybierz lotnisko',
      path: ['airportCode'],
    },
  );

export const transportSchema = z
  .object({
    transportOffering: z.enum(TransportOffering, {
      error: 'Pole jest wymagane',
    }),
    selectionMode: z.enum(TransportSelectionMode).nullish(),
    variants: z.array(transportVariantSchema),
    // TFG regulatory territory — required for every tour (see features/dashboard/tfg)
    territory: z.enum(Territory).optional(),
  })
  .refine((data) => data.territory != null, {
    message: 'Wybierz terytorium',
    path: ['territory'],
  })
  .refine(
    (data) =>
      data.transportOffering !== TransportOffering.OFFERS_TRANSPORT ||
      data.selectionMode != null,
    {
      message: 'Wybierz sposób wyboru transportu',
      path: ['selectionMode'],
    },
  )
  .refine(
    (data) =>
      data.transportOffering !== TransportOffering.OFFERS_TRANSPORT ||
      data.variants.length >= 1,
    {
      message: 'Dodaj co najmniej jeden wariant transportu',
      path: ['variants'],
    },
  )
  .transform((data) => {
    if (data.transportOffering === TransportOffering.NO_TRANSPORT) {
      return { ...data, selectionMode: null, variants: [] };
    }
    return data;
  });

export type TransportFormData = z.infer<typeof transportSchema>;
export type TransportInputForm = z.input<typeof transportSchema>;
export type TransportVariantFormData = z.infer<typeof transportVariantSchema>;

export const emptyTransportVariant =
  (): TransportInputForm['variants'][number] => ({
    name: '',
    description: '',
    participantLimitMode: ParticipantLimitMode.UNLIMITED,
    participantLimit: undefined,
    currency: Currency.PLN,
    isPriceByAge: false,
    flatPrice: undefined,
    ageRangePrices: [],
    ufgTransportType: undefined,
    airportCode: undefined,
  });

export const transportDefaultValues: TransportInputForm = {
  transportOffering: TransportOffering.OFFERS_TRANSPORT,
  selectionMode: TransportSelectionMode.MANDATORY,
  variants: [emptyTransportVariant()],
  territory: undefined,
};
